import React, { useState } from 'react';
import { X, Save, Loader2, AlertCircle } from 'lucide-react';
import { atlasApi, AtlasUser, Vaga } from '../shared/atlasApi';

interface Props {
  user: AtlasUser;
  vaga?: Vaga | null;
  onClose: () => void;
  onSalvo: () => void;
}

const inputCls = 'w-full px-3 py-2 rounded-lg border border-gray-200 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-purple-200 focus:border-purple-400';
const labelCls = 'text-[10px] text-gray-400 uppercase tracking-wider font-semibold mb-1 block';

const ModalNova: React.FC<Props> = ({ user, vaga, onClose, onSalvo }) => {
  const ed = vaga || null;
  const [form, setForm] = useState({
    titulo: ed?.titulo || '',
    descricao: ed?.descricao || '',
    requisitos: ed?.requisitos || '',
    beneficios: ed?.beneficios || '',
    tipo_contrato: ed?.tipo_contrato || 'clt',
    modalidade: ed?.modalidade || 'presencial',
    departamento: ed?.departamento?.toString() || '',
    salario_min: ed?.salario_min?.toString() || '',
    salario_max: ed?.salario_max?.toString() || '',
    salario_combinar: ed?.salario_combinar || false,
    status: ed?.status || 'rascunho',
  });
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const set = (k: keyof typeof form, v: any) => setForm(p => ({ ...p, [k]: v }));

  const salvar = async () => {
    if (!form.titulo.trim()) { setErro('Título é obrigatório.'); return; }
    setSalvando(true); setErro(null);
    const body: Record<string, any> = {
      ...form,
      salario_min: form.salario_min ? Number(form.salario_min) : undefined,
      salario_max: form.salario_max ? Number(form.salario_max) : undefined,
    };
    try {
      if (ed) await atlasApi.editarVaga(user, ed._id, body);
      else await atlasApi.criarVaga(user, body);
      onSalvo();
    } catch (e: any) { setErro(e.message || 'Erro ao salvar.'); setSalvando(false); }
  };

  return (
    <div className="fixed inset-0 z-[10050] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white max-w-2xl w-full max-h-[90vh] overflow-y-auto rounded-2xl shadow-2xl" onClick={e => e.stopPropagation()}>
        <div className="flex items-center gap-3 p-5 border-b border-gray-100 sticky top-0 bg-white z-10">
          <h3 className="font-bold text-gray-900 flex-1">{ed ? 'Editar vaga' : 'Nova vaga'}</h3>
          <button onClick={onClose} className="p-2 rounded-lg text-gray-400 hover:bg-gray-100"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-3">
          <div><label className={labelCls}>Título *</label><input className={inputCls} value={form.titulo} onChange={e => set('titulo', e.target.value)} /></div>
          <div><label className={labelCls}>Descrição</label><textarea className={`${inputCls} min-h-[70px]`} value={form.descricao} onChange={e => set('descricao', e.target.value)} /></div>
          <div><label className={labelCls}>Requisitos</label><textarea className={`${inputCls} min-h-[60px]`} value={form.requisitos} onChange={e => set('requisitos', e.target.value)} /></div>
          <div><label className={labelCls}>Benefícios</label><textarea className={`${inputCls} min-h-[60px]`} value={form.beneficios} onChange={e => set('beneficios', e.target.value)} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Tipo de contrato</label>
              <select className={inputCls} value={form.tipo_contrato} onChange={e => set('tipo_contrato', e.target.value)}>
                <option value="clt">CLT</option><option value="pj">PJ</option><option value="estagio">Estágio</option><option value="temp">Temporário</option>
              </select>
            </div>
            <div>
              <label className={labelCls}>Modalidade</label>
              <select className={inputCls} value={form.modalidade} onChange={e => set('modalidade', e.target.value)}>
                <option value="presencial">Presencial</option><option value="remoto">Remoto</option><option value="hibrido">Híbrido</option>
              </select>
            </div>
            <div><label className={labelCls}>Departamento (cód)</label><input className={inputCls} value={form.departamento} onChange={e => set('departamento', e.target.value)} /></div>
            <div>
              <label className={labelCls}>Status</label>
              <select className={inputCls} value={form.status} onChange={e => set('status', e.target.value)}>
                <option value="rascunho">Rascunho</option><option value="aberta">Aberta</option><option value="encerrada">Encerrada</option><option value="preenchida">Preenchida</option>
              </select>
            </div>
            <div><label className={labelCls}>Salário mín.</label><input className={inputCls} type="number" value={form.salario_min} onChange={e => set('salario_min', e.target.value)} disabled={form.salario_combinar} /></div>
            <div><label className={labelCls}>Salário máx.</label><input className={inputCls} type="number" value={form.salario_max} onChange={e => set('salario_max', e.target.value)} disabled={form.salario_combinar} /></div>
          </div>
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input type="checkbox" checked={form.salario_combinar} onChange={e => set('salario_combinar', e.target.checked)} /> Salário a combinar
          </label>

          {erro && <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-rose-50 border border-rose-200 text-rose-700 text-sm"><AlertCircle className="w-4 h-4" /> {erro}</div>}

          <div className="flex justify-end gap-2 pt-1">
            <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">Cancelar</button>
            <button onClick={salvar} disabled={salvando} className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white text-sm font-medium rounded-lg">
              {salvando ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Salvar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ModalNova;
